// ====================================================================================
// COMPONENT: CandidateMinervaBriefing (Aesthetic: Dark Glass & High-Fidelity)
// Self-contained with no-data state, four-column briefing, and all icons.
// ====================================================================================
import {
  AlertCircle,
  AlertTriangle,
  UserCheck,
  Zap,
  Target,
  TrendingUp,
  Network,
  Sparkles,
  ShieldCheck,
} from "lucide-react";

const CandidateMinervaBriefing = ({ profileData }) => {

  // --- Empty State (Glass Modal) ---
  const renderNoDataState = () => (
    <div className="absolute inset-0 z-20 flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-sm rounded-xl transition-all duration-500">
      <div className="text-center bg-slate-900/90 border border-slate-700 p-8 rounded-xl shadow-2xl max-w-md">
        <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4 border border-slate-700">
          <AlertCircle className="text-amber-400" size={24} />
        </div>
        <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-2">Profile Unavailable</h3>
        <p className="text-sm text-slate-400 leading-relaxed">Candidate intelligence has not yet been synthesised. Please advise the candidate to complete the onboarding survey.</p>
      </div>
    </div>
  );

  const renderDataState = () => {
    const formatActionItem = (item) => {
      if (!item || item === "N/A" || item === "None") return item;
      return item.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    const name = profileData.metadata?.candidate_name || "N/A";
    const persona = profileData.candidate_synthesis?.personality_persona?.persona_name || "Balanced";
    const moralFrame = profileData.north_star_narrative?.moral_frame || "N/A";
    const resiliencePersona = profileData.candidate_synthesis?.resilience_persona?.resilience_archetype || "Generally Resilient";
    const commsPersona = profileData.candidate_synthesis?.communication_persona?.comm_type || "Adaptive";
    const resilienceRisk = profileData.candidate_synthesis?.resilience_persona?.primary_risk || "No significant resilience risks identified.";
    const isWarning = resilienceRisk.startsWith("WARNING:");
    const resilienceRiskText = resilienceRisk.replace("WARNING: ", "");

    const compatibility = (profileData.strengths_analysis?.strategic_advantages.find(({ advantage }) => advantage === "Ward Compatibility")?.score || 0) * 100;
    const topResonance = profileData.theia_electorate_analysis?.campaign_resonance_analysis?.top_resonant_archetypes?.[0]?.archetype_name || "N/A";
    const lowResonance = profileData.theia_electorate_analysis?.campaign_resonance_analysis?.lowest_resonance_archetypes?.[0]?.archetype_name || "N/A";

    const topStrength = formatActionItem(profileData.strengths_analysis?.personality_strengths?.[0]?.dimension || profileData.strengths_analysis?.communication_strengths?.[0]?.dimension || "N/A");
    const topGap = profileData.authenticity_gap_analysis?.gaps?.[0]?.target_archetype || "None";
    const topPriority = profileData.development_priorities?.[0]?.area || "N/A";

    // Helper for micro-labels
    const Label = ({ text }) => <h4 className="text-[9px] text-slate-500 uppercase font-bold tracking-widest mb-1.5 shrink-0">{text}</h4>;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 relative z-10">

        {/* === Column 1: IDENTITY === */}
        <div className="flex flex-col bg-slate-950/30 p-5 rounded-lg border border-slate-800 hover:border-slate-600 hover:bg-slate-900/40 transition-all duration-300 min-h-[360px] group">
          <div className="flex items-center space-x-2.5 mb-5 shrink-0 pb-3 border-b border-slate-800/50 group-hover:border-slate-700/50 transition-colors">
            <UserCheck className="text-white" size={16} />
            <h3 className="text-[10px] font-bold text-slate-300 uppercase tracking-widest group-hover:text-white transition-colors">Identity Matrix</h3>
          </div>

          <div className="space-y-5 flex-grow">
            <div className="min-w-0">
              <Label text="Candidate Name" />
              <p className="text-lg font-bold text-white leading-tight break-words tracking-tight">{name}</p>
            </div>
            <div>
              <Label text="Personality Persona" />
              <div className="inline-block px-2 py-1 rounded bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-bold uppercase tracking-wide">
                {persona}
              </div>
            </div>
            <div>
              <Label text="Resilience Archetype" />
              <p className={`text-sm font-semibold ${isWarning ? 'text-rose-400' : 'text-emerald-400'}`}>{resiliencePersona}</p>
            </div>
          </div>

          <div className="pt-4 mt-2 border-t border-slate-800/50 shrink-0">
            <Label text="Moral Frame" />
            <p className="text-xs text-slate-300 font-medium leading-relaxed">{moralFrame}</p>
          </div>
        </div>

        {/* === Column 2: VECTORS === */}
        <div className="flex flex-col bg-slate-950/30 p-5 rounded-lg border border-slate-800 hover:border-slate-600 hover:bg-slate-900/40 transition-all duration-300 min-h-[360px] group">
          <div className="flex items-center space-x-2.5 mb-5 shrink-0 pb-3 border-b border-slate-800/50 group-hover:border-slate-700/50 transition-colors">
            <Zap className="text-white" size={16} />
            <h3 className="text-[10px] font-bold text-slate-300 uppercase tracking-widest group-hover:text-white transition-colors">Strategic Vectors</h3>
          </div>

          <div className="flex-grow space-y-5">
            <div>
              <Label text="Communication Style" />
              <p className="text-sm text-white font-semibold leading-relaxed">{commsPersona}</p>
            </div>
          </div>

          <div className="pt-4 mt-auto border-t border-slate-800/50 shrink-0">
            <div className="flex items-center gap-2 mb-1.5">
              {isWarning ? <AlertTriangle size={12} className="text-rose-400" /> : <ShieldCheck size={12} className="text-slate-500" />}
              <Label text="Primary Risk Vector" />
            </div>
            <p className={`text-xs leading-relaxed font-medium ${isWarning ? 'text-rose-300' : 'text-slate-500'}`}>
              {resilienceRiskText}
            </p>
          </div>
        </div>

        {/* === Column 3: RESONANCE === */}
        <div className="flex flex-col bg-slate-950/30 p-5 rounded-lg border border-slate-800 hover:border-slate-600 hover:bg-slate-900/40 transition-all duration-300 min-h-[360px] group">
          <div className="mb-5 shrink-0 pb-3 border-b border-slate-800/50 group-hover:border-slate-700/50 transition-colors flex justify-between items-start">
            <div className="flex items-center space-x-2.5">
              <Target className="text-white" size={16} />
              <h3 className="text-[10px] font-bold text-slate-300 uppercase tracking-widest group-hover:text-white transition-colors">Resonance</h3>
            </div>
            <div className="flex flex-col items-end">
              <span className={`text-lg font-bold tabular-nums leading-none ${compatibility > 70 ? 'text-emerald-400' : 'text-amber-400'}`}>
                {compatibility.toFixed(0)}%
              </span>
              <span className="text-[8px] text-slate-500 uppercase tracking-widest font-bold mt-0.5">Match</span>
            </div>
          </div>

          <div className="space-y-6 flex-grow">
            <div>
              <Label text="Highest Resonance" />
              <div className="flex items-center gap-2 mt-1">
                <div className="w-1 h-8 bg-emerald-500/50 rounded-full"></div>
                <p className="text-sm text-white font-semibold leading-snug">{topResonance}</p>
              </div>
            </div>
            <div>
              <Label text="Lowest Resonance" />
              <div className="flex items-center gap-2 mt-1">
                <div className="w-1 h-8 bg-rose-500/50 rounded-full"></div>
                <p className="text-sm text-slate-300 font-medium leading-snug">{lowResonance}</p>
              </div>
            </div>
          </div>

          <div className="pt-4 mt-auto border-t border-slate-800/50 shrink-0">
            <div className="flex justify-between text-[9px] text-slate-500 uppercase font-bold mb-1.5">
              <span>Compatibility Index</span>
              <span>{compatibility.toFixed(0)}/100</span>
            </div>
            <div className="h-1.5 w-full bg-slate-900 rounded-full overflow-hidden border border-slate-800">
              <div
                className={`h-full rounded-full transition-all duration-1000 ${compatibility > 70 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                style={{ width: `${compatibility}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* === Column 4: ACTION PLAN === */}
        <div className="flex flex-col bg-slate-950/30 p-5 rounded-lg border border-slate-800 hover:border-slate-600 hover:bg-slate-900/40 transition-all duration-300 min-h-[360px] group">
          <div className="flex items-center space-x-2.5 mb-5 shrink-0 pb-3 border-b border-slate-800/50 group-hover:border-slate-700/50 transition-colors">
            <TrendingUp className="text-white" size={16} />
            <h3 className="text-[10px] font-bold text-slate-300 uppercase tracking-widest group-hover:text-white transition-colors">Action Plan</h3>
          </div>

          <div className="flex-grow space-y-5">
            <div>
              <Label text="Amplify (Core Strength)" />
              <div className="flex items-start gap-2">
                <Sparkles size={12} className="text-amber-400 mt-0.5 shrink-0" />
                <p className="text-sm text-white font-bold leading-tight">{topStrength}</p>
              </div>
            </div>

            <div>
              <Label text="Bridge (Authenticity Gap)" />
              <div className="flex items-start gap-2">
                <Network size={12} className="text-indigo-400 mt-0.5 shrink-0" />
                <p className="text-xs text-slate-300 font-medium leading-snug">{topGap}</p>
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-4 mt-auto border-t border-slate-800/50 shrink-0">
            <div>
              <Label text="Development Priority" />
              <div className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 mt-1">
                <p className="text-xs text-white font-mono">{topPriority}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-slate-900/40 border border-slate-700/50 rounded-xl p-6 relative overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6 border-b border-slate-800/50 pb-4">
        <div className="p-1.5 bg-slate-800 rounded-md border border-slate-700">
          <UserCheck className="text-white" size={14} />
        </div>
        <div>
          <h3 className="text-s font-bold text-white uppercase tracking-widest">Candidate Briefing</h3>
          <p className="text-[12px] text-slate-500 font-medium">Candidate Intelligence Synthesis</p>
        </div>
      </div>

      {!profileData && renderNoDataState()}
      <div className={!profileData ? "blur-sm opacity-50 pointer-events-none" : ""}>
        {profileData ? renderDataState() : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-[360px] bg-slate-800/20 rounded-lg animate-pulse border border-slate-800/30" />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateMinervaBriefing;
